import { Box, HStack, Text, VStack } from "@chakra-ui/react"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { useAugmentationSubpanelReturn } from "@pathwright/ux/src/components/augmentation/useAugmentationSubpanel"
import { AUGMENTATION_PANEL_INPUT_PROPS } from "@pathwright/ux/src/components/augmentation/augmentationPanelStyles"
import { toaster } from "@pathwright/ux/src/components/chakra/ui/toaster"
import Field from "@pathwright/ux/src/components/form/Field"
import { FormikWithDiscardFormState } from "@pathwright/ux/src/components/form/state/FormikDiscardFormState"
import KeyboardFormSubmit from "@pathwright/ux/src/components/form/state/KeyboardFormSubmit"
import { Form, FormikProps } from "formik"
import { useMemo } from "react"
import { z } from "zod"
import { toFormikValidate } from "zod-formik-adapter"
import {
  OfferKind,
  useCreateGroupMutation
} from "@pathwright/graphql-client/src/generated"
import {
  useCohortsUpgradeInlineGate,
  type UseCohortsUpgradeInlineGateReturn
} from "../../cohort/create/UpgradeCohortsGate"
import { useCoverPageContext } from "../CoverPageProvider"
import { useCoverPageIsEditable } from "../access"
import { useCoverPageTranslate } from "../utils"
import {
  COVER_PAGE_NEW_OFFER_PANEL_PATH,
  CoverPagePanelOfferValues,
  NEW_COHORT_VISIBILITY,
  addCohortToOfferableCohortsCache,
  buildOfferInitialValues,
  getNewOfferFormStateKey
} from "./CoverPagePanelOffer"
import { CoverPagePanelFormActions } from "./CoverPagePanelFormActions"
import { useCoverPagePanelErrorMessage } from "./utils"

const cohortSchema = z.object({ name: z.string() })

type CoverPagePanelOfferCohortValues = z.infer<typeof cohortSchema>

// Shown in place of the form when the plan's in-progress Cohort limit for this
// Path is reached.
function CohortsUpgradeNotice({
  gate
}: {
  gate: NonNullable<Exclude<UseCohortsUpgradeInlineGateReturn, false>>
}) {
  return (
    <HStack align="flex-start" gap={2} pt="15px">
      <Box pt="2px" flexShrink={0}>
        <Pathicon icon={gate.icon} />
      </Box>
      <VStack align="stretch" gap={1}>
        <Text m={0} fontSize="sm" fontWeight="semibold">
          {gate.heading}
        </Text>
        <Box fontSize="sm" color="fg.muted" css={{ "& p": { m: 0 } }}>
          {gate.body}
        </Box>
      </VStack>
    </HStack>
  )
}

// Opened from the New offer panel's Cohort picker. Creates the Cohort, then
// returns to the offer draft with it selected.
export function CoverPagePanelOfferCohortCreateRoute() {
  const { t } = useCoverPageTranslate()
  const getErrorMessage = useCoverPagePanelErrorMessage()
  const isEditable = useCoverPageIsEditable()
  const resourceId = useCoverPageContext((value) => value.resourceId)
  const [createCohort] = useCreateGroupMutation()
  // false while the plan check loads, a gate when the plan is at its limit.
  const upgradeGate = useCohortsUpgradeInlineGate(resourceId)
  const canCreate = upgradeGate === null

  const offerInitialValues = useMemo(() => buildOfferInitialValues(null, t), [t])
  const subpanel = useAugmentationSubpanelReturn<CoverPagePanelOfferValues>({
    parentPath: COVER_PAGE_NEW_OFFER_PANEL_PATH,
    parentFormStateKey: getNewOfferFormStateKey(resourceId),
    parentInitialValues: offerInitialValues
  })

  // Suggest the offer's title, since the two usually match.
  const initialValues = useMemo(
    () => ({ name: subpanel.readParentDraft().title.trim() }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const validate = useMemo(
    () =>
      toFormikValidate(
        cohortSchema.superRefine((values, ctx) => {
          if (!values.name.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ["name"],
              message: t("panel_offer_cohort.name_required", {
                defaultValue: "Enter a Cohort name."
              })
            })
          }
        })
      ),
    [t]
  )

  async function handleSubmit(values: CoverPagePanelOfferCohortValues) {
    try {
      if (!isEditable)
        throw new Error("You do not have permission to edit this Cover Page.")
      if (!canCreate) return

      const result = await createCohort({
        variables: {
          base_resource_id: resourceId,
          visibility: NEW_COHORT_VISIBILITY,
          name: values.name.trim(),
          role: null
        },
        update: (cache, { data }) => {
          const cohort = data?.createGroup
          if (cohort) addCohortToOfferableCohortsCache(cache, resourceId, cohort)
        }
      })
      const cohortId = result.data?.createGroup?.id

      if (!cohortId) {
        throw new Error(
          t("panel_offer.create_cohort_failed", {
            defaultValue: "Cohort could not be created."
          })
        )
      }

      subpanel.returnWith({
        kind: OfferKind.JoinCohort,
        targetCohortId: String(cohortId)
      })
    } catch (error) {
      toaster.create({
        title: getErrorMessage(
          error,
          t("panel_errors.create_cohort", {
            defaultValue: "Error creating Cohort:"
          })
        ),
        type: "error",
        duration: 4000,
        closable: true
      })
      throw error
    }
  }

  return (
    <FormikWithDiscardFormState
      formStateKey={[...getNewOfferFormStateKey(resourceId), "cohort"]}
      initialValues={initialValues}
      validate={validate}
      validateOnMount
      formStateOnSubmit="clearOnSuccess"
      onSubmit={handleSubmit}
    >
      {(form: FormikProps<CoverPagePanelOfferCohortValues>) => {
        const formId = "cover-page-panel-offer-cohort"

        return (
          <Form
            id={formId}
            style={{
              display: "flex",
              flexDirection: "column",
              width: "100%"
            }}
          >
            <KeyboardFormSubmit />
            {upgradeGate ? (
              <CohortsUpgradeNotice gate={upgradeGate} />
            ) : (
              <VStack align="stretch" gap="15px" w="full" pt="15px">
                <Field
                  {...AUGMENTATION_PANEL_INPUT_PROPS}
                  name="name"
                  label={t("panel_offer_cohort.cohort_name", {
                    defaultValue: "Cohort name"
                  })}
                  placeholder={t(
                    "panel_offer_cohort.cohort_name_placeholder",
                    {
                      defaultValue:
                        "Enter a Cohort name (Fall session, Study along...)"
                    }
                  )}
                  disabled={!isEditable || form.isSubmitting}
                />
              </VStack>
            )}
            <CoverPagePanelFormActions
              disabled={!isEditable || !canCreate}
              form={form}
              formId={formId}
              requireDirty={false}
              submit={{
                children: t("panel_offer_cohort.create_cohort", {
                  defaultValue: "Create Cohort"
                })
              }}
              discard={{
                children: t("panel.cancel", { defaultValue: "Cancel" }),
                // Cancel is always available, unlike Discard, which needs edits.
                disabled: form.isSubmitting
              }}
              onDiscard={subpanel.cancel}
            />
          </Form>
        )
      }}
    </FormikWithDiscardFormState>
  )
}
